import * as React from 'react';

import { PlanType } from '@showbie-socrative/socrative-utils';
import { translate } from '~l10n/client';

import { PaywallTouchEventAdapter } from '~analytics/index';
import { PaywallScreens } from '~analytics/types';
import { UIButton } from '~shared/components/buttons';
import { Dialog, DialogProps, DialogTitle } from '~shared/components/Dialog';
import { Inline } from '~shared/components/Inline';
import { Link } from '~shared/components/Link';
import { Stack } from '~shared/components/Stack';
import { proCheck } from '~teacher/util/proCheck';

import { PaywallUpgradeButton } from './PaywallUpgradeButton';

export interface Props extends Pick<DialogProps, 'open' | 'onClose'> {
  paywallScreen: PaywallScreens;
}

export function PaywallDialogGeneric({ open, onClose, paywallScreen }: Props) {
  React.useEffect(() => {
    if (open) {
      new PaywallTouchEventAdapter(paywallScreen).sendAnalytics();
    }
  }, [open, paywallScreen]);

  const userPlan = proCheck.userPlan();
  const features =
    userPlan === PlanType.FreeLegacy
      ? [
          {
            title: translate('Run multiple quizzes at once'),
            text: translate(
              'Launch multiple activities at the same time for all your classes.'
            ),
          },
          {
            title: translate('Create a roster'),
            text: translate(
              'A Roster lets you choose which students have access to a room’s activities.'
            ),
          },
          {
            title: translate('Weighted questions'),
            text: translate(
              'Adjust the amount of points each question is worth. Then give students full or partial marks for their answer.'
            ),
          },
        ]
      : [
          {
            title: translate('Unlimited quizzes'),
            /** @todo Get actual number from Quiz Limit query. */
            text: translate(
              'Create and import as many quizzes as you like. Your free account is limited to {0} quizzes.'
            ).format('5'),
          },
          {
            title: translate('Run multiple quizzes at once'),
            text: translate(
              'Launch multiple activities at the same time for all your classes.'
            ),
          },
          {
            title: translate('Email individual reports'),
            text: translate(
              'Customize reports to include explanations, correct answers, multiple choice answers, and download answer keys.'
            ),
          },
        ];

  return (
    <Dialog
      id="paywall"
      title={translate('Do More with Socrative Pro')}
      padding="confirmation"
      open={open}
      onClose={onClose}
      closeLabel={translate('Close')}
    >
      {(headerProps) => (
        <Stack space="8">
          <h3 className="m-0">
            <DialogTitle ref={headerProps.headingRef}>
              {headerProps.title}
            </DialogTitle>
          </h3>
          <dl className="m-0 font-sans text-sm">
            <Stack space="8">
              {features.map((feature, i) => (
                <div key={i} className="leading-8 text-blue-900">
                  <dt className="font-500">{feature.title}</dt>
                  <dd className="m-0">{feature.text}</dd>
                </div>
              ))}
            </Stack>
          </dl>
          <div className="font-sans text-sm">
            <Link
              href="https://www.socrative.com/plans/"
              target="_blank"
              rel="noopener noreferrer"
            >
              {translate('Learn more about Socrative Pro')}
            </Link>
          </div>
          <Inline align="right">
            <UIButton key="cancel" onClick={onClose}>
              {translate('Cancel')}
            </UIButton>
            <PaywallUpgradeButton
              key="upgrade"
              appearance="primary"
              paywallScreen={paywallScreen}
            />
          </Inline>
        </Stack>
      )}
    </Dialog>
  );
}
